import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { deleteHttp, post } from "../../common/api/apiShared";
import { _arrayBufferToBase64 } from "../../common/arrayBufferToBase64";
import { useDotLegalMutation, useDotLegalQueryArray, useDotLegalQueryClient } from "../../common/hooks/dotLegalReactQuery";
import { useTranslation } from "../../localization/useTranslation";
import { AiChatAttachment, AiChatAttachmentLimits, AiChatAttachmentStatus } from "../AiChat.types";

export const aiChatAttachmentsQueryKey = "ai-chat-attachments";

export function useAiChatAttachments(conversationId: string | null) {
    return useDotLegalQueryArray<AiChatAttachment[]>([aiChatAttachmentsQueryKey, conversationId], `/ai-chat/conversations/${conversationId}/attachments`, {
        enabled: conversationId !== null,
        refetchInterval: (query) => {
            const data = query.state.data as AiChatAttachment[] | undefined;
            const anyProcessing = data?.some((a) => a.status === AiChatAttachmentStatus.Processing) ?? false;
            return anyProcessing ? 1000 : false;
        },
    });
}

export interface UploadAiChatAttachmentVariables {
    conversationId: string;
    file: File;
}

export function useUploadAiChatAttachment() {
    const queryClient = useDotLegalQueryClient();
    return useDotLegalMutation<AiChatAttachment, UploadAiChatAttachmentVariables>(
        async ({ conversationId, file }) => {
            const buffer = await file.arrayBuffer();
            const base64Data = await _arrayBufferToBase64(buffer);
            const result = await post<AiChatAttachment>(`/ai-chat/conversations/${conversationId}/attachments`, {
                fileName: file.name,
                contentType: file.type,
                base64Data,
            });
            return result.value();
        },
        {
            onSuccess: () => {
                queryClient.refetchQueries(aiChatAttachmentsQueryKey);
            },
        }
    );
}

export function useDeleteAiChatAttachment() {
    const queryClient = useDotLegalQueryClient();
    return useDotLegalMutation<unknown, string>((attachmentId) => deleteHttp(`/ai-chat/attachments/${attachmentId}`), {
        onSuccess: () => {
            queryClient.refetchQueries(aiChatAttachmentsQueryKey);
        },
    });
}

interface UseAiChatAttachmentUploadProps {
    ensureConversationId: () => Promise<string>;
    currentAttachmentCount: number;
}

export function useAiChatAttachmentUpload({ ensureConversationId, currentAttachmentCount }: UseAiChatAttachmentUploadProps) {
    const uploadMutation = useUploadAiChatAttachment();
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    async function uploadFiles(input: File[] | FileList) {
        const incoming = Array.from(input);
        if (incoming.length === 0) return;

        if (currentAttachmentCount + incoming.length > AiChatAttachmentLimits.maxAttachmentsPerConversation) {
            snackbar.show(translateString("aiChatAttachmentLimitReached"), "warning");
            return;
        }

        const valid: File[] = [];
        for (const file of incoming) {
            const dot = file.name.lastIndexOf(".");
            const extension = dot === -1 ? "" : file.name.slice(dot).toLowerCase();
            if (!AiChatAttachmentLimits.allowedExtensions.includes(extension as (typeof AiChatAttachmentLimits.allowedExtensions)[number])) {
                snackbar.show(translateString("aiChatAttachmentUnsupportedType"), "warning");
                continue;
            }
            if (file.size > AiChatAttachmentLimits.maxFileSizeBytes) {
                snackbar.show(translateString("aiChatAttachmentTooLarge"), "warning");
                continue;
            }
            valid.push(file);
        }

        if (valid.length === 0) return;

        const conversationId = await ensureConversationId();
        for (const file of valid) {
            uploadMutation.mutate({ conversationId, file });
        }
    }

    return { uploadFiles };
}
